/**
 * Picks a city, then a theme inside it.
 *
 * Replaces the old multi-city routing. If the traveller has already booked
 * flights, the city is given and only the theme is chosen — the swipe deck
 * still runs, because it is what matches the sidekick and orders the days.
 */
import { ACTIVE_CATALOG, type ActiveCity } from "./market.js";
import { expScore } from "./scoring.js";
import { THEMES, themesFor, type CityTheme } from "./themes.js";
import { isPersonaCard } from "./types.js";
import type { Axes, Card, Uni } from "./types.js";

const byId = (id: string) => ACTIVE_CATALOG.find((c) => c.id === id);

/** Mean fit of a theme's anchor experiences against the taste read. */
export function themeScore(theme: CityTheme, uni: Uni, axes: Axes): number {
  const anchors = theme.anchors.map(byId).filter((e) => e !== undefined);
  if (!anchors.length) return 0;
  return anchors.reduce((sum, e) => sum + expScore(e!, uni, axes), 0) / anchors.length;
}

/**
 * How much of what they actually swiped right on belongs to each city.
 *
 * This is the strongest available signal and it was missing at first: theme
 * fit alone compares abstract taste axes, and three tier-one cities overlap
 * heavily on those. Someone who liked eight Shanghai cards should go to
 * Shanghai, whatever the axis maths says.
 */
export function cityAffinity(likes: Card[]): Record<string, number> {
  const real = likes.filter((c) => !isPersonaCard(c));
  const out: Record<string, number> = {};
  if (!real.length) return out;
  for (const c of real) {
    const city = (c as { city: string }).city;
    out[city] = (out[city] ?? 0) + 1 / real.length;
  }
  return out;
}

/** Weight on direct city affinity. High enough to dominate theme fit, which
 * barely separates three cities that all do food, nightlife and skylines. */
const AFFINITY_WEIGHT = 6;

/**
 * Best city for this traveller: what they liked, then how well the city's
 * strongest theme fits.
 */
export function pickCity(uni: Uni, axes: Axes, kids: boolean, likes: Card[] = []): ActiveCity {
  const affinity = cityAffinity(likes);
  const cities = [...new Set(THEMES.map((t) => t.city))];
  let best = cities[0]!;
  let bestScore = -Infinity;
  for (const city of cities) {
    const themes = themesFor(city);
    // A city is worth as much as its single strongest theme, not its average —
    // one compelling reason to go beats three mediocre ones.
    let score = Math.max(...themes.map((t) => themeScore(t, uni, axes)));
    score += (affinity[city] ?? 0) * AFFINITY_WEIGHT;
    if (kids) {
      // With kids, weight toward the gentlest theme the city can offer.
      const easiest = Math.max(
        ...themes.map((t) => {
          const anchors = t.anchors.map(byId).filter((e) => e !== undefined);
          if (!anchors.length) return 0;
          return anchors.reduce((s, e) => s + e!.family, 0) / anchors.length;
        }),
      );
      score += easiest * 0.6;
    }
    if (score > bestScore) {
      bestScore = score;
      best = city;
    }
  }
  return best;
}

/** Best theme within a city. */
export function pickTheme(city: ActiveCity, uni: Uni, axes: Axes, kids: boolean): CityTheme {
  const themes = themesFor(city);
  let best = themes[0]!;
  let bestScore = -Infinity;
  for (const t of themes) {
    let score = themeScore(t, uni, axes);
    if (kids) {
      const anchors = t.anchors.map(byId).filter((e) => e !== undefined);
      const family = anchors.length
        ? anchors.reduce((s, e) => s + e!.family, 0) / anchors.length
        : 0;
      score += family * 0.8;
    }
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  return best;
}

/**
 * The whole routing decision.
 *
 * @param lockedCity set when the traveller has already booked flights — the
 *   city is then a given and only the theme is chosen.
 */
export function routeUser(
  uni: Uni,
  axes: Axes,
  kids: boolean,
  lockedCity?: ActiveCity,
  likes: Card[] = [],
): CityTheme {
  const city = lockedCity ?? pickCity(uni, axes, kids, likes);
  return pickTheme(city, uni, axes, kids);
}
